import { useEffect, useMemo } from "react";

import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import DynamicPhoneNumber from "common/blocks/DynamicPhoneNumber";
import RichText from "common/blocks/RichText";
import useModalContext from "common/providers/modal/useModalContext";
import {
    deliveryOrCollectionDetailedModal,
    deliveryOrCollectionFormModal,
} from "features/vehicleDetails/modals";
import CollectionTransferFindOutMore from "features/vehicleDetails/modals/CollectionTransferFindOutMore";
import useClosestStoresContext from "features/vehicleDetails/providers/closestStores/useClosestStoresContext";
import { ResolvedRichText } from "resolvers/data/richTextResolver";
import { ResolvedSidebarButton } from "resolvers/data/sidebarLinkResolver";
import formatDateTime from "utils/formatDateTime";
import formatPrice from "utils/formatPrice";

import styles from "./CollectionTransferDeliveryWidget.module.scss";
import TransferDeliveryRow from "./TransferDeliveryRow";

const cx = classNames.bind(styles);

export type CollectionTransferDeliveryWidgetProps = {
    readyForCollectionText: ResolvedRichText;
    readyForCollectionDateTime: string;
    readyForCollectionPrice: string;
    freeText: string;
    vehicleTransferAvailableText: string;
    enterYourPostcodeText: string;
    homeDeliveryAvailableText: string;
    afterPhoneNumberText: string;
    storeDirectionUrl: string;
    getDirectionButtonText: string;
    editPostcodeButtonText: string;
    submitPostcodeButtonText: string;
    enterPostcodeFreeText: ResolvedRichText;
    privacyPolicyInfo: ResolvedRichText;
    detailedInfoFreeText: ResolvedRichText;
    removePostcodeButtonText: string;
    seeStoreDetailsButtonText: string;
    homeDeliveryPrice: string;
    findOutMoreSidebarButton: ResolvedSidebarButton;
    findOutMoreText: string;
    showHomeDelivery: boolean;
};

const CollectionTransferDeliveryWidget = (props: CollectionTransferDeliveryWidgetProps) => {
    const {
        afterPhoneNumberText,
        enterYourPostcodeText,
        findOutMoreText,
        homeDeliveryAvailableText,
        freeText,
        readyForCollectionText,
        readyForCollectionDateTime,
        vehicleTransferAvailableText,
        storeDirectionUrl,
        getDirectionButtonText,
        editPostcodeButtonText,
        homeDeliveryPrice,
        findOutMoreSidebarButton,
        showHomeDelivery,
    } = props;
    const { useSidebar } = useModalContext();
    const { openSidebar, registerSidebars, openRegisteredSidebar } = useSidebar();

    const transformedModals = useMemo(() => {
        if (findOutMoreSidebarButton.sidebars) {
            return Object.keys(findOutMoreSidebarButton.sidebars).reduce(
                (res, key) => {
                    res[key] = {
                        title: findOutMoreSidebarButton.sidebars?.[key].title,
                        content: {
                            component: CollectionTransferFindOutMore,
                            props: findOutMoreSidebarButton?.sidebars?.[key].content,
                        },
                    };
                    return res;
                },
                {} as { [key: string]: any }
            );
        }
        return {};
    }, [findOutMoreSidebarButton.sidebars]);

    useEffect(() => {
        if (Object.keys(transformedModals).length) {
            registerSidebars(transformedModals);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transformedModals]);

    const { closestStores, hasClosestStores } = useClosestStoresContext();

    const content = useMemo(() => {
        const readyForCollection = (
            <TransferDeliveryRow
                key="ready-for-collection"
                icon={hasClosestStores ? "PropertyLocation2" : "PropertyLocation"}
                price={freeText}
            >
                <RichText {...readyForCollectionText} />
                <span className={cx("fromDate")}>{readyForCollectionDateTime}</span>
                <div className={cx("buttonRow")}>
                    <Button
                        type="button"
                        theme="textLink"
                        href={storeDirectionUrl}
                        target="_blank"
                        ariaLabel={getDirectionButtonText}
                    >
                        {getDirectionButtonText}
                    </Button>
                </div>
            </TransferDeliveryRow>
        );

        const contentArray = [readyForCollection];

        if (hasClosestStores) {
            contentArray.push(
                <TransferDeliveryRow key="transfer-to-store" icon="PropertyLocation">
                    <div className={cx("contentWrapper")}>
                        <p>{vehicleTransferAvailableText}</p>
                        <ul className={cx("stores")}>
                            {closestStores.map((store) => (
                                <li key={store.siteId}>
                                    <span className={cx("title")}>
                                        <span>
                                            {store.siteName} - {store.address.postcode}
                                        </span>
                                        <span className={cx("price")}>
                                            {formatPrice(store.bookingSlot.price, freeText)}
                                        </span>
                                    </span>
                                    <span className={cx("fromDate")}>
                                        from {formatDateTime(store.bookingSlot.date)}.
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <div className={cx("buttonRow")}>
                            <Button
                                type="button"
                                theme="textLink"
                                onClick={() => {
                                    openSidebar(deliveryOrCollectionDetailedModal, props);
                                }}
                                ariaLabel={findOutMoreText}
                            >
                                {findOutMoreText}
                            </Button>
                            <Button
                                type="button"
                                theme="textLink"
                                onClick={() => {
                                    openSidebar(deliveryOrCollectionFormModal, props);
                                }}
                                ariaLabel={editPostcodeButtonText}
                            >
                                {editPostcodeButtonText}
                            </Button>
                        </div>
                    </div>
                </TransferDeliveryRow>
            );
        }

        if (!hasClosestStores) {
            contentArray.push(
                <TransferDeliveryRow key="transfer-available" icon="Map">
                    <p>{vehicleTransferAvailableText}</p>
                    <div className={cx("buttonRow")}>
                        <Button
                            type="button"
                            theme="textLink"
                            onClick={() => openSidebar(deliveryOrCollectionFormModal, props)}
                            ariaLabel={enterYourPostcodeText}
                        >
                            {enterYourPostcodeText}
                        </Button>
                        <Button
                            type="button"
                            theme="textLink"
                            ariaLabel={findOutMoreSidebarButton.label}
                            onClick={() =>
                                openRegisteredSidebar(findOutMoreSidebarButton.params.sidebarId, {
                                    openByDefault: findOutMoreSidebarButton.params.anchor,
                                })
                            }
                        >
                            {findOutMoreSidebarButton.label}
                        </Button>
                    </div>
                </TransferDeliveryRow>
            );
        }

        if (showHomeDelivery) {
            contentArray.push(
                <TransferDeliveryRow
                    key="home-delivery"
                    icon="DeliveryVan"
                    price={homeDeliveryPrice}
                >
                    <p>
                        {homeDeliveryAvailableText}.
                        <br />
                        Call <DynamicPhoneNumber isLink className={cx("phoneNumber")} />{" "}
                        {afterPhoneNumberText}
                    </p>
                </TransferDeliveryRow>
            );
        }

        return contentArray;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showHomeDelivery, hasClosestStores]);

    return (
        <div
            id="collection-transfer-delivery-wiget"
            className={cx("collectionTransferDeliveryWidget")}
        >
            {content}
        </div>
    );
};

export default CollectionTransferDeliveryWidget;
